import React, { useState, useContext } from "react";
import {
  Button,
  TextField,
  Checkbox,
  Grid,
  Skeleton,
  IconButton,
  Collapse,
  Alert,
  Box,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Delete,
  Folder,
  FolderOpen,
  InsertDriveFile,
  CloudUpload,
  Info,
} from "@mui/icons-material";
import { encodeS3Path } from "../../../../Components/helpers/Helpers";
import {
  DocsContext,
  PermissionContext,
} from "../../../../Components/Context/Context";
import { CustomIconsPlusText } from "../../../../Components/Common/Design/CustomIconsPlusText";
import api from "../../../../api/axios";

const ArchivosMostrar = ({
  caseId,
  userLogged,
  setDocsChangedFlag,
  docsChangedFlag,
  selectedDocs,
  setSelectedDocs,
  dataCase,
}) => {
  const Swal = require("sweetalert2");
  const { can } = useContext(PermissionContext);
  const { docsInfo, docsInfoTransfered, docFlagTransfered, docsFlag } =
    useContext(DocsContext);

  const [openFolders, setOpenFolders] = useState({});

  const toggleFolder = (folderKey) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderKey]: !prev[folderKey],
    }));
  };

  // -------------------------
  // RENOMBRAR DOCUMENTO
  // -------------------------
  const handleNewDocumentName = async (fileNewTitle, fileOldName, fullpath) => {
    const getInputId = document.getElementById(fileNewTitle);
    if (!getInputId.value) {
      return Swal.fire({
        title: "No se ha detectado el nombre del archivo",
        icon: "warning",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }

    const result = await Swal.fire({
      title: "¿Desea guardar el nombre del documento?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Guardando...",
      text: "Por favor espera un momento",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const formData = new FormData();
      formData.append("case_id", caseId);
      formData.append("doc_title", getInputId.value);
      formData.append("doc_file", fullpath);
      formData.append("doc_status", 1);
      formData.append("user_id", userLogged?.id);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/docs/newdoctitle`,
        formData,
      );
      Swal.close();

      if (data?.error?.errorInfo?.length > 0) {
        Swal.fire({
          title: "Error",
          text: "¡Ha ocurrido un error al subir el nombre del documento!",
          icon: "error",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
      } else {
        Swal.fire({
          title: "¡Guardado correctamente!",
          icon: "success",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        setDocsChangedFlag(!docsChangedFlag);
      }
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Error",
        text: "No se pudo completar la solicitud.",
        icon: "error",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
      console.error(error);
    }
  };

  // -------------------------
  // ELIMINAR DOCUMENTO
  // -------------------------
  const handleDelete = async (file) => {
    const formData = new FormData();
    formData.append("case_id", caseId);
    formData.append("full_path", file?.path);
    formData.append("doc_id", file?.doc_id || 0);

    const response = await Swal.fire({
      title: "¿Seguro que desea eliminar el archivo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!response.isConfirmed) return;

    Swal.fire({
      title: "Eliminando archivos...",
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading(),
    });

    try {
      await api.post(`${process.env.REACT_APP_API_URL}/s3/destroy`, formData);
      Swal.close();
      Swal.fire({
        title: "¡Eliminado!",
        icon: "success",
        text: "La documentación se ha eliminado exitosamente",
        confirmButtonColor: "#1ab394",
      });
      setDocsChangedFlag(!docsChangedFlag);
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Error",
        text: `Error al eliminar: ${error.message}`,
        icon: "error",
        confirmButtonColor: "#1ab394",
      });
      console.error(error);
    }
  };

  // -------------------------
  // SELECT CARPETA / DOCUMENTO
  // -------------------------
  const handleSelectFolder = (folderKey, files, checked) => {
    setSelectedDocs((prev) => {
      const updated = { ...prev };
      updated[folderKey] = checked
        ? files.map((f) => ({ doc_id: f.doc_id, path: f.path }))
        : [];
      return updated;
    });

    // Abrir la carpeta solo si estaba cerrada y se está marcando
    if (checked && !openFolders[folderKey]) {
      setOpenFolders((prev) => ({
        ...prev,
        [folderKey]: true,
      }));
    }
  };

  const handleSelectDoc = (folderKey, file, checked) => {
    setSelectedDocs((prev) => {
      const updated = { ...prev };
      const docs = updated[folderKey] ? [...updated[folderKey]] : [];
      if (checked) {
        if (!docs.some((d) => d.doc_id === file.doc_id && d.path === file.path))
          docs.push({ doc_id: file.doc_id, path: file.path });
      } else {
        updated[folderKey] = docs.filter(
          (d) => !(d.doc_id === file.doc_id && d.path === file.path),
        );
        return updated;
      }
      updated[folderKey] = docs;
      return updated;
    });
  };

  // -------------------------
  // RENDER DOCUMENTOS
  // -------------------------
  const renderDocumentSection = (
    docsData,
    sectionTitle,
    transfered = false,
    docFlag = true,
  ) => {
    if (!docFlag) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!docsData || Object.entries(docsData).length === 0) {
      if (!docFlagTransfered && !transfered) {
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            No hay documentación para mostrar
          </Alert>
        );
      }
      return null;
    }

    const groupedByCompany = {};
    Object.entries(docsData).forEach(([caseId, folders]) => {
      Object.entries(folders).forEach(([folderName, files]) => {
        files.forEach((file) => {
          const companyName =
            file?.company_relation?.company_name ||
            file?.unmatched_doc?.case_relation?.company_relation
              ?.company_name ||
            file?.matched_doc?.case_relation?.company_relation?.company_name ||
            "Sin nombre asignado";
          if (!groupedByCompany[companyName])
            groupedByCompany[companyName] = {};
          if (!groupedByCompany[companyName][caseId])
            groupedByCompany[companyName][caseId] = {};
          if (!groupedByCompany[companyName][caseId][folderName])
            groupedByCompany[companyName][caseId][folderName] = [];
          groupedByCompany[companyName][caseId][folderName].push(file);
        });
      });
    });

    return (
      <Box sx={{ width: "100%", mb: 4 }}>
        {sectionTitle && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            {sectionTitle}
          </Typography>
        )}

        {Object.entries(groupedByCompany).map(([companyName, cases]) => (
          <Paper
            key={companyName}
            sx={{ p: 2, mb: 2, backgroundColor: "#f9f9f9" }}>
            <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
              {companyName}
            </Typography>

            {Object.entries(cases).map(([caseId, folders]) => (
              <Box key={caseId} sx={{ ml: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Expediente #{caseId}
                </Typography>

                {Object.entries(folders).map(([folderName, files]) => {
                  files.sort((a, b) => {
                    if (a.matched && !b.matched) return -1;
                    if (!a.matched && b.matched) return 1;
                    if (a.matched && b.matched)
                      return new Date(b.date_in) - new Date(a.date_in);
                    return 0;
                  });

                  const folderKey = `${caseId}-${folderName}`;
                  const isOpen = openFolders[folderKey] ?? false;

                  return (
                    <Paper
                      key={folderKey}
                      sx={{
                        mb: 1,
                        p: 1,
                        borderLeft: "4px solid #1a73e8",
                        cursor: "pointer",
                        backgroundColor: "#f9f9f9",
                      }}>
                      <Grid
                        container
                        alignItems="center"
                        sx={{
                          width: "100%",
                        }}
                        onClick={() => toggleFolder(folderKey)}>
                        <Grid item xs={1}>
                          {!transfered && (
                            <Checkbox
                              checked={
                                (selectedDocs[folderKey]?.length || 0) ===
                                files.length
                              }
                              indeterminate={
                                selectedDocs[folderKey]?.length > 0 &&
                                selectedDocs[folderKey]?.length < files.length
                              }
                              onChange={(e) => {
                                e.stopPropagation(); // evita toggle por el click en Paper
                                handleSelectFolder(
                                  folderKey,
                                  files,
                                  e.target.checked,
                                );
                              }}
                              sx={{ mr: 1 }}
                            />
                          )}
                        </Grid>
                        <Grid
                          item
                          xs={11}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}>
                          {isOpen ? (
                            <FolderOpen color="warning" sx={{ mr: 1 }} />
                          ) : (
                            <Folder color="warning" sx={{ mr: 1 }} />
                          )}
                          <Typography variant="body1">{folderName}</Typography>
                        </Grid>
                      </Grid>

                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        {files.map((file, index) => {
                          const fileInputId = `${folderKey}-file-${index}`;
                          return (
                            <Paper
                              key={fileInputId}
                              sx={{
                                p: 1,
                                mt: 1,
                                backgroundColor: "#fff",
                                transition: "background-color 0.5s ease",
                                "&:hover": {
                                  backgroundColor: "#f0f0f0", // color un poco más oscuro
                                },
                              }}>
                              <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between">
                                <Grid
                                  item
                                  xs={10}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}>
                                  {!transfered && (
                                    <Checkbox
                                      checked={selectedDocs[folderKey]?.some(
                                        (d) =>
                                          d.doc_id === file.doc_id &&
                                          d.path === file.path,
                                      )}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleSelectDoc(
                                          folderKey,
                                          file,
                                          e.target.checked,
                                        );
                                      }}
                                      sx={{ mr: 1 }}
                                    />
                                  )}
                                  <InsertDriveFile
                                    color="info"
                                    sx={{ mr: 1 }}
                                  />
                                  <a
                                    href={`${process.env.REACT_APP_S3_API_URL}/${encodeS3Path(file.path)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      textDecoration: "none",
                                      color: "#000",
                                      fontWeight: file.matched ? 700 : 400,
                                    }}>
                                    {file.matched
                                      ? file.doc_title
                                      : file.file_name}
                                  </a>
                                  {file.doc_desc && (
                                    <CustomIconsPlusText
                                      icon={Info}
                                      severity={
                                        file.doc_desc.includes("Recibido")
                                          ? "info"
                                          : "warning"
                                      }
                                      styles={{ width: 15, height: 15, ml: 1 }}
                                      text={file.doc_desc}
                                    />
                                  )}
                                </Grid>

                                {(file?.unmatched_doc?.case_relation
                                  ?.company_relation?.id ===
                                  dataCase?.company_relation?.id ||
                                  file?.matched_doc?.case_relation
                                    ?.company_relation?.id ===
                                    dataCase?.company_relation?.id) &&
                                  can("cases", "deletedocs") && (
                                    <Grid
                                      item
                                      xs={2}
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                      }}>
                                      <IconButton
                                        color="error"
                                        onClick={() => handleDelete(file)}>
                                        <Delete />
                                      </IconButton>
                                    </Grid>
                                  )}
                              </Grid>

                              {!file.matched && can("cases", "docs") && (
                                <Box sx={{ display: "flex", mt: 1 }}>
                                  <TextField
                                    size="small"
                                    label="Nombrar archivo"
                                    id={fileInputId}
                                    sx={{ mr: 1, flex: 1 }}
                                  />
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() =>
                                      handleNewDocumentName(
                                        fileInputId,
                                        file.file_name,
                                        file.path,
                                      )
                                    }>
                                    <CloudUpload />
                                  </Button>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  mt: 1,
                                  fontStyle: "italic",
                                  color: "gray",
                                }}>
                                {file.matched ? (
                                  <>
                                    <div>Subido por: {file?.lawyer_name}</div>
                                    <div>Fecha: {file?.date_in}</div>
                                  </>
                                ) : (
                                  <div>El archivo aún no ha sido nombrado</div>
                                )}
                              </Box>
                            </Paper>
                          );
                        })}
                      </Collapse>
                    </Paper>
                  );
                })}
              </Box>
            ))}
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, width: "100%" }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Solo tiene permitido eliminar información del caso actual.
      </Alert>
      {renderDocumentSection(
        docsInfo,
        `Documentación del caso en ${dataCase?.company_name}`,
        false,
        !!docsInfo,
      )}
      {renderDocumentSection(
        docsInfoTransfered,
        "Documentos transferidos",
        true,
        !!docsInfoTransfered,
      )}
    </Box>
  );
};

export default ArchivosMostrar;
